import { useState } from 'react';
import { Modal } from './Modal';
import { ModalFooter } from './ModalFooter';
import { ToggleSwitch } from './ToggleSwitch';
import { FormGroup } from './FormGroup';
import { SpinnerInput } from './SpinnerInput';
import { useCreateBatch } from '../hooks/useProjectBatches';
import { useResourceSelectionStore } from '../stores/resourceSelectionStore';
import { useResourceViewerStore } from '../stores/resourceViewerStore';
import type { BulkContext } from './AddTagControl';

/**
 * Create Batches — React port of Angular's AddBatchModalController / dialog_batch.html.
 * Opens a batch-setup modal over the current grid selection. mode 'some' sends the
 * explicit resource ids; mode 'all' omits them so the server batches everything
 * matching the filter.
 */
export function CreateBatchButton({ bulk }: { bulk: BulkContext }) {
  const mode = useResourceSelectionStore((s) => s.mode);
  const selectedCount = useResourceSelectionStore((s) => Object.keys(s.selected).length);
  const [open, setOpen] = useState(false);
  const hasSelection = mode === 'all' || selectedCount > 0;

  return (
    <>
      <button
        type="button"
        className="btn btn-default"
        disabled={!hasSelection}
        onClick={() => setOpen(true)}
        title="Create Batches"
      >
        <i className="fas fa-cubes" />
      </button>
      {open && <CreateBatchModal bulk={bulk} onClose={() => setOpen(false)} />}
    </>
  );
}

function pad(value: number, length: number): string {
  return String(Math.max(0, value || 0)).padStart(Math.max(0, length || 0), '0');
}

function CreateBatchModal({ bulk, onClose }: { bulk: BulkContext; onClose: () => void }) {
  const mode = useResourceSelectionStore((s) => s.mode);
  const selected = useResourceSelectionStore((s) => s.selected);
  const clear = useResourceSelectionStore((s) => s.clear);
  const totalRows = useResourceViewerStore((s) => s.totalRows);
  const count = mode === 'all' ? totalRows : Object.keys(selected).length;

  const [prefix, setPrefix] = useState('');
  const [delimiter, setDelimiter] = useState('_');
  const [startValue, setStartValue] = useState(1);
  const [zeroPaddingLength, setZeroPaddingLength] = useState(3);
  const [filesPerBatch, setFilesPerBatch] = useState(200);
  const [keepFamiliesTogether, setKeepFamiliesTogether] = useState(true);
  const [includeRelated, setIncludeRelated] = useState(false);

  const createBatch = useCreateBatch(bulk.projectId);

  const preview = `${prefix}${delimiter}${pad(startValue, zeroPaddingLength)}`;
  const valid =
    prefix.trim().length > 0 &&
    delimiter.length === 1 &&
    Number.isFinite(startValue) &&
    zeroPaddingLength >= 0 &&
    filesPerBatch > 0;

  const submit = () => {
    if (!valid) return;
    createBatch.mutate(
      {
        projectId: bulk.projectId,
        prefix,
        delimiter,
        startValue,
        zeroPaddingLength,
        filesPerBatch,
        keepFamiliesTogether,
        includeRelated,
        collections: bulk.collectionIds,
        filter: bulk.filter,
        columnFilters: bulk.columnFilters ?? null,
        resources: mode === 'all' ? undefined : Object.keys(selected),
        // AddBatchingModalController:42-43 — the search scopes the batch only in
        // select-all mode (Angular: `&& !resources`); an explicit id list wins.
        searchExpression: mode === 'all' && bulk.search?.advanced ? bulk.search.expression : null,
        query: mode === 'all' && bulk.search?.advanced === false ? bulk.search.query : null,
      },
      { onSuccess: () => { clear(); onClose(); } }
    );
  };

  return (
    <Modal
      title="Batch Setup"
      icon="fas fa-share"
      onClose={onClose}
      onSubmit={(e) => { e.preventDefault(); submit(); }}
      formClassName="form-horizontal"
      footer={
        <ModalFooter
          onClose={onClose}
          nextLabel="Start"
          nextDisabled={!valid}
          saving={createBatch.isPending}
          savingLabel="Processing"
        />
      }
    >
      <p className="text-muted">
        Creating batches from {count.toLocaleString()} resource{count === 1 ? '' : 's'}.
      </p>

      <FormGroup label="Prefix" htmlFor="batch-prefix">
        <input id="batch-prefix" className="form-control" value={prefix} maxLength={255}
          onChange={(e) => setPrefix(e.target.value)} disabled={createBatch.isPending} />
      </FormGroup>
      <FormGroup label="Delimiter" htmlFor="batch-delimiter">
        <input id="batch-delimiter" className="form-control" value={delimiter} maxLength={1}
          onChange={(e) => setDelimiter(e.target.value)} disabled={createBatch.isPending} />
      </FormGroup>
      {/* SpinnerInput instead of a native number input: the browser spinners repeat
          continuously on a single click (same bug the Angular iio-number-spinner fixes). */}
      <FormGroup label="Start Value" htmlFor="batch-start">
        <SpinnerInput id="batch-start" value={startValue} onChange={setStartValue}
          min={0} max={9999999999} disabled={createBatch.isPending} />
      </FormGroup>
      <FormGroup label="Zero Padding Length" htmlFor="batch-pad">
        <SpinnerInput id="batch-pad" value={zeroPaddingLength} onChange={setZeroPaddingLength}
          min={0} max={99} disabled={createBatch.isPending} />
      </FormGroup>
      <FormGroup label="Preview" htmlFor="batch-preview">
        <p id="batch-preview" className="form-control-static">{preview}</p>
      </FormGroup>
      <FormGroup label="Files Per Batch" htmlFor="batch-fpb">
        <SpinnerInput id="batch-fpb" value={filesPerBatch} onChange={setFilesPerBatch}
          min={1} max={99999} disabled={createBatch.isPending} />
      </FormGroup>
      <FormGroup label="Keep Families Together">
        <ToggleSwitch checked={keepFamiliesTogether} onChange={setKeepFamiliesTogether} disabled={createBatch.isPending} />
      </FormGroup>
      <FormGroup label="Include Related Items">
        <ToggleSwitch checked={includeRelated} onChange={setIncludeRelated} disabled={createBatch.isPending} />
      </FormGroup>
    </Modal>
  );
}
